// The wizard's map (board 25 v2 §أ): the counter counts STEPS (owner 6 —
// 5 for seats, which have no step 5 — member 4), the thin progress line
// counts SCREENS («ما اسمك؟» is one more screen of the account step, not a
// step). The business step alone is five screens (six with the branches
// question) because each answer fills something in the preview.
// Step 5 and the sixth step follow the category's shape (data/shapes.ts).

import { toArabicDigits } from '@/lib/digits';
import { STEP_LABELS } from './copy';
import { SHAPES, type BookingShape } from './data/shapes';

export type ScreenKey =
  | 'role'
  | 'join'
  | 'phone'
  | 'otp'
  | 'pin'
  | 'pin-confirm'
  | 'you'
  | 'name'
  | 'type'
  | 'city'
  | 'branches'
  | 'address'
  | 'bphone'
  | 'hours'
  | 'workdays'
  | 'handover'
  | 'shape'
  | 'done'
  | 'waiting'
  | 'accepted';

export interface PathOptions {
  intent: 'owner' | 'member';
  /** Owner: the category's shape (null = no category yet → the staff map). */
  shape: BookingShape | null;
  /** Owner: the platform has branches on → «عندك أكثر من فرع؟» after the city. */
  branchesQuestion: boolean;
  /** Member: came with an invite code (else «تابع برقمك» → waiting). */
  withCode: boolean;
}

const STEP5_KEYS: ReadonlySet<ScreenKey> = new Set<ScreenKey>(['hours', 'workdays', 'handover']);

function specOf(o: PathOptions) {
  return SHAPES[o.shape ?? 'staff'];
}

export function screensOf(o: PathOptions): ScreenKey[] {
  if (o.intent === 'member') {
    return ['role', ...(o.withCode ? (['join'] as ScreenKey[]) : []), 'phone', 'otp', 'pin', 'pin-confirm', o.withCode ? 'accepted' : 'waiting'];
  }
  const step5 = specOf(o).step5;
  return [
    'role',
    'phone',
    'otp',
    'pin',
    'pin-confirm',
    'you',
    'name',
    'type',
    'city',
    ...(o.branchesQuestion ? (['branches'] as ScreenKey[]) : []),
    'address',
    'bphone',
    ...(step5 === 'none' ? [] : ([step5] as ScreenKey[])),
    'shape',
    'done',
  ];
}

/** Where `key` stands on the path (-1 = off it); a step-5 screen of another shape stands where this path's step 5 stands. */
export function screenIndex(key: ScreenKey, o: PathOptions, screens: ScreenKey[] = screensOf(o)): number {
  const i = screens.indexOf(key);
  if (i < 0 && STEP5_KEYS.has(key)) return screens.findIndex((k) => STEP5_KEYS.has(k));
  return i;
}

/** 0–100, the thin line above the column: screens done out of the path's screens. */
export function progressOf(key: ScreenKey, o: PathOptions): number {
  const screens = screensOf(o);
  const i = screenIndex(key, o, screens);
  if (i < 0) return 100;
  return Math.round(((i + 1) / screens.length) * 100);
}

export interface StepInfo {
  n: number;
  total: number;
  label: string;
}

/**
 * The «n/N · label» counter; null on the screens outside the counted path
 * (waiting, accepted, done). `shapeLabel` names the sixth step for the chosen
 * category (the place noun); absent, the shape's generic label.
 */
export function stepOf(key: ScreenKey, o: PathOptions, shapeLabel = ''): StepInfo | null {
  if (o.intent === 'member') {
    const total = o.withCode ? 4 : 4;
    const map: Partial<Record<ScreenKey, [number, string]>> = {
      role: [1, STEP_LABELS.role],
      join: [2, STEP_LABELS.join],
      phone: [o.withCode ? 3 : 2, STEP_LABELS.phone],
      otp: [o.withCode ? 3 : 2, STEP_LABELS.phone],
      pin: [o.withCode ? 4 : 3, STEP_LABELS.pin],
      'pin-confirm': [o.withCode ? 4 : 3, STEP_LABELS.pin],
    };
    const m = map[key];
    return m ? { n: m[0], total: o.withCode ? total : 3, label: m[1] } : null;
  }
  const spec = specOf(o);
  const total = spec.step5 === 'none' ? 5 : 6;
  const map: Partial<Record<ScreenKey, [number, string]>> = {
    role: [1, STEP_LABELS.role],
    phone: [2, STEP_LABELS.phone],
    otp: [2, STEP_LABELS.phone],
    pin: [3, STEP_LABELS.pin],
    'pin-confirm': [3, STEP_LABELS.pin],
    // «ما اسمك؟» stays inside the account step — the counter has no seventh step.
    you: [3, STEP_LABELS.pin],
    name: [4, STEP_LABELS.business],
    type: [4, STEP_LABELS.business],
    city: [4, STEP_LABELS.business],
    branches: [4, STEP_LABELS.business],
    address: [4, STEP_LABELS.business],
    bphone: [4, STEP_LABELS.business],
    hours: [5, STEP_LABELS.hours],
    workdays: [5, STEP_LABELS.workdays],
    handover: [5, STEP_LABELS.handover],
    shape: [total, shapeLabel || spec.stepLabel('default', '')],
  };
  const m = map[key];
  return m ? { n: m[0], total, label: m[1] } : null;
}

/** «١/٦ · الدور» */
export function counterText(s: StepInfo | null): string {
  if (!s) return '';
  return `${toArabicDigits(s.n)}/${toArabicDigits(s.total)} · ${s.label}`;
}
